import { useAppConfig } from "../AppContext";

/**
 * Standard, config-aware date/time formatting for the whole app.
 *
 * STANDING RULE (see CLAUDE.md "Configuration system"): every date or time
 * shown anywhere in i-Track must be formatted through these functions (or the
 * useDateTimeFormat() hook below), never with a component-local fmtDate/fmtTime,
 * never with a hardcoded pattern, and never with a bare .toLocaleDateString().
 * App Settings → Date Format / Time Format says "Applied to all dates" —
 * this module is what makes that literally true instead of aspirational.
 *
 * Before this module existed, ~10 different files each had their own
 * fmtDate/fmtTime that ignored app_config entirely (hardcoded to one format),
 * which is why the Billing Hub (and others) drifted out of sync with App
 * Settings. Route every new date/time display through here.
 */

export function fmtDate(iso, fmt = "MM/DD/YYYY") {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return "—";
  if (fmt === "DD/MM/YYYY") return `${d}/${m}/${y}`;
  if (fmt === "YYYY-MM-DD") return `${y}-${m}-${d}`;
  return `${m}/${d}/${y}`; // MM/DD/YYYY default
}

export function fmtTime(timeStr, fmt = "12h") {
  if (!timeStr) return "—";
  const [hStr, mStr] = String(timeStr).slice(0, 5).split(":");
  const h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return "—";
  const mm = mStr || "00";
  if (fmt === "24h") return `${String(h).padStart(2, "0")}:${mm}`;
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${ampm}`;
}

/** Formats a SQL/ISO datetime string ("2026-08-23 14:13:35" or with a "T")
 * as "<date> <time>" using both configured formats in one call — the common
 * case for "executed_at" / "created_at" / "started_at" columns. */
export function fmtDateTime(value, dateFmt = "MM/DD/YYYY", timeFmt = "12h") {
  if (!value) return "—";
  const [datePart, timePart] = String(value).replace("T", " ").split(" ");
  const d = fmtDate(datePart, dateFmt);
  if (!timePart) return d;
  return `${d} ${fmtTime(timePart.slice(0, 5), timeFmt)}`;
}

/**
 * Convenience hook — bound formatters that read the live app config, so a
 * component never threads date_format/time_format through props by hand.
 * This is the preferred way to consume formatting in any component:
 *
 *   const { fmtDate, fmtTime, fmtDateTime } = useDateTimeFormat();
 *   <td>{fmtDate(row.next_run_date)}</td>
 */
export function useDateTimeFormat() {
  const { config } = useAppConfig();
  const dateFmt = config?.date_format || "MM/DD/YYYY";
  const timeFmt = config?.time_format || "12h";
  return {
    dateFmt,
    timeFmt,
    fmtDate: (iso) => fmtDate(iso, dateFmt),
    fmtTime: (t) => fmtTime(t, timeFmt),
    fmtDateTime: (v) => fmtDateTime(v, dateFmt, timeFmt),
  };
}
